// 마을②~⑥ 환경(S7, docs/STAGE-WORLD-PRD.md 5.1; ⑤⑥은 3개월 C2): 마을①(EnvVisual 광장·벽·고원)과 다른 판 — 용암 바다 위 성채 섬 · 겨울 항구 · 늪 데크 · 청록 샘 오아시스 · 구름 위 요새.
// 구성(StageEnv와 같은 규약 — 판정 격자에서 보이는 것만 만든다): 렌더 격자(걸을 수 있는 바닥 + 섬 + 건물 칸) → 거리장 등고선(stageEdge) →
//   바닥 윗면(마을 판석·널판) · 둑/흉벽 단면 · 가장자리 트림(S2 세트피스) · 액체 면(용암·바다·늪) · 다리·원경 세트피스 ·
//   시설 건물(마을 키트, 정면은 그 NPC 쪽) · 마을 전용 Blender 세트피스(public/assets/town) · 섬 위 집 · 소품 · 주민(NPC) · 풀/눈/포자/불꽃.
// 모든 로드는 start() 하나로 끝나고(실패한 조각은 빠진다), 프레임마다 하는 일은 cull()·animate()뿐이다(EnvVisual과 같은 규약).
import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { float, mix, mx_noise_float, positionWorld, smoothstep, time, vec2, vec3 } from 'three/tsl';
import type { QualityLevel } from '../../shared/config';
import { rasterize, type MapDef, type MapGrid, type Rect } from '../../shared/levels';
import { townNpcs } from '../../shared/rpg';
import { cellsOf, rectsOfCells } from '../../shared/shapes';
import { TOWN_LOOKS, gateFront, type TownLook, type TownPropKind, type TownSetName } from '../../shared/villageMaps';
import { compose } from '../util';
import { magmaMaterial } from './emberTerrain';
import { cardField, contactField, driftField, flameField, glowField, ivyField } from './fx';
import type { EnvResult } from './index';
import { assetUrl, loadKit, loadTexture, track, type KitAsset } from './kits';
import type { Emitter, KitKind } from './layout';
import { moodOf } from './mood';
import { VillageNpcs, townSpots } from './npcs';
import { REGION_LOOK, backdropMaterial, kitMaterial } from './surfaces';
import { mulberry32, valueNoise } from './rng';
import { loadSet, loadSetAtlas, setMaterial, type SetAsset, type SetAtlas, type SetName } from './sets';
import { STYLE_MODULES } from './stageSets';
import { buildField, contourOf, edgeProximity, edgeStyleAt, resample, shoreDistance, type Chain } from './stageEdge';
import { dataTexture, stageCliffMaterial, stageFloorMaterial, stageWaterMaterial, type ProximityTex } from './stageSurfaces';

const CULL_MARGIN = 14;
const CULL_RADIUS = 44;
const _ndc = new THREE.Vector3();
const _dir = new THREE.Vector3();
const DENSITY: Record<QualityLevel, number> = { low: 0.5, mid: 0.75, high: 1 };
const PI = Math.PI;

type CutMode = 0 | 1 | 2;
interface Inst { x: number; y: number; z: number; rot: number; sx: number; sy: number; sz: number; tint: number; cut: CutMode; radius: number }
interface Batch { cullR: number; mesh: THREE.InstancedMesh; cut: THREE.InstancedBufferAttribute; n: number; mat: Float32Array; cutSrc: Float32Array; col: Float32Array; pos: Float32Array }

/** 마을 전용 세트피스 재질 규칙: 카메라 쪽 컷(clip = 낮게 자르기, 아니면 디더), 단면 색 */
const TOWN_SET_STYLE: Readonly<Record<TownSetName, { clip: boolean; cut: CutMode; cap: readonly [number, number, number]; shadow: boolean }>> = {
  great_forge: { clip: false, cut: 2, cap: [0.2, 0.18, 0.2], shadow: true },
  obsidian_spire: { clip: false, cut: 2, cap: [0.16, 0.15, 0.18], shadow: true },
  ship: { clip: false, cut: 2, cap: [0.34, 0.25, 0.15], shadow: true },
  lighthouse: { clip: false, cut: 2, cap: [0.42, 0.44, 0.4], shadow: true },
  stilt_house: { clip: false, cut: 2, cap: [0.34, 0.25, 0.15], shadow: true },
  cauldron: { clip: true, cut: 1, cap: [0.2, 0.2, 0.22], shadow: false },
};
/** 세트피스의 불빛 자리(로컬 좌표, 정면 +z 기준): 불꽃 또는 발광 망울 */
const TOWN_SET_LIGHTS: Readonly<Partial<Record<TownSetName, readonly { x: number; y: number; z: number; s: number; kind: 'flame' | 'glow'; rgb?: readonly [number, number, number] }[]>>> = {
  great_forge: [{ x: 0.9, y: 9.4, z: -1.6, s: 1.3, kind: 'flame' }, { x: 0, y: 0.7, z: 1.2, s: 1.4, kind: 'flame' }, { x: 0, y: 1.3, z: 1.0, s: 3.2, kind: 'glow', rgb: [1.0, 0.45, 0.12] }],
  obsidian_spire: [{ x: 1.55, y: 1.15, z: 1.55, s: 0.9, kind: 'flame' }, { x: 0, y: 2.0, z: 0, s: 4.0, kind: 'glow', rgb: [1.0, 0.35, 0.08] }],
  ship: [{ x: 0, y: 3.9, z: -6.35, s: 1.4, kind: 'glow', rgb: [1.0, 0.7, 0.3] }],
  lighthouse: [{ x: 0, y: 8.2, z: 0, s: 3.2, kind: 'glow', rgb: [1.0, 0.75, 0.35] }],
  stilt_house: [{ x: 1.6, y: 2.8, z: 2.3, s: 1.2, kind: 'glow', rgb: [1.0, 0.7, 0.3] }],
  cauldron: [{ x: 0, y: 1.25, z: 0, s: 2.2, kind: 'glow', rgb: [0.4, 1.0, 0.35] }, { x: 0, y: 0.35, z: 0, s: 0.7, kind: 'flame' }],
};
/** 원경 절벽 색(마을별): 화산 · 눈 · 이끼 · 사구 · 보랏빛 성채 능선 */
const FAR_TINT: Readonly<Record<number, number>> = { 2: 0x6a5056, 3: 0xe2ecf8, 4: 0x8c9c7a, 5: 0xdcb078, 6: 0x8c78b8 };
/** 가장자리 트림 색: ② 흑요석 흉벽 · ③ 젖은 돌 둑 · ④ 늪 뿌리 · ⑤ 사암 둑 · ⑥ 흰 돌 흉벽 */
const TRIM_TINT: Readonly<Record<number, readonly number[]>> = {
  2: [0x6c6470, 0x7a7078, 0x5c5660], 3: [0xe6eef8, 0xd4dee8, 0xf2f6fc], 4: [0xb8c49c, 0xa8b48a, 0xc4cca8],
  5: [0xecd4a4, 0xdcc08c, 0xf4e0b4], 6: [0xf2f6ff, 0xdfe8f6, 0xeaf0fb],
};
/** 마을별 색: 시설 건물 · 문 · 노점 곱, 불빛 망울(glow)·가로등 불빛 색. 없는 마을은 늪(④) 값 */
interface TownTints { facility: number; gate: number; stall: number; glow: readonly [number, number, number]; lamp: readonly [number, number, number] }
const TOWN_TINTS: Readonly<Record<number, TownTints>> = {
  2: { facility: 0xd8c4bc, gate: 0xff7040, stall: 0xc8a8a0, glow: [1.0, 0.42, 0.12], lamp: [0.9, 0.42, 0.12] },
  3: { facility: 0xeef2fa, gate: 0xd8e8ff, stall: 0xe8f0ff, glow: [1.0, 0.72, 0.4], lamp: [0.7, 0.85, 1.0] },
  4: { facility: 0xe4ead6, gate: 0xd4e8c0, stall: 0xd8e8c0, glow: [1.0, 0.42, 0.12], lamp: [0.9, 0.42, 0.12] },
  5: { facility: 0xf2dcae, gate: 0xf2d494, stall: 0xe8c890, glow: [0.45, 0.95, 0.85], lamp: [1.0, 0.72, 0.3] },
  6: { facility: 0xf0f4ff, gate: 0xe4eeff, stall: 0xe8f0ff, glow: [0.6, 0.82, 1.0], lamp: [0.75, 0.88, 1.0] },
};

/** 렌더 격자: 걸을 수 있는 바닥 + 섬(걸을 수 없는 집 터) + 건물 칸. 판정 격자와 따로(섬은 판정에 없다) */
function renderGrid(map: MapDef, look: TownLook): MapGrid {
  return rasterize({ ...map, floors: [...map.floors, ...look.islands], gates: [], lava: [] });
}

/** 마을 전용 세트피스 로더(public/assets/town/town_<이름>.glb, 공용 아틀라스 UV) */
let townLoader: GLTFLoader | null = null;
const townCache = new Map<TownSetName, Promise<SetAsset | null>>();
function loadTownSet(name: TownSetName): Promise<SetAsset | null> {
  let p = townCache.get(name);
  if (!p) {
    p = (async () => {
      try {
        townLoader ??= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
        const gltf = await townLoader.loadAsync(assetUrl('town/town_' + name + '.glb'));
        let mesh: THREE.Mesh | null = null;
        gltf.scene.traverse((o) => { if (!mesh && (o as THREE.Mesh).isMesh) mesh = o as THREE.Mesh; });
        if (!mesh) throw new Error('메시 없음');
        const src = mesh as THREE.Mesh;
        src.updateWorldMatrix(true, false);
        const geometry = src.geometry.clone();
        for (const key of Object.keys(geometry.attributes)) if (key !== 'position' && key !== 'normal' && key !== 'uv') geometry.deleteAttribute(key);
        for (const a of ['position', 'normal', 'uv']) {
          const at = geometry.getAttribute(a) as THREE.BufferAttribute | THREE.InterleavedBufferAttribute | undefined;
          if (!at) continue;
          const inter = (at as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute === true;
          if (!inter && at.array instanceof Float32Array && !at.normalized) continue;
          const s = at.itemSize, n = at.count, out = new Float32Array(n * s);
          for (let i = 0; i < n; i++) { out[i * s] = at.getX(i); if (s > 1) out[i * s + 1] = at.getY(i); if (s > 2) out[i * s + 2] = at.getZ(i); }
          geometry.setAttribute(a, new THREE.BufferAttribute(out, s));
        }
        geometry.applyMatrix4(src.matrixWorld);
        if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
        geometry.computeBoundingBox();
        const bb = geometry.boundingBox!;
        (src.material as THREE.Material | undefined)?.dispose();
        src.geometry.dispose();
        const triangles = geometry.index ? geometry.index.count / 3 : geometry.getAttribute('position').count / 3;
        return { name: name as unknown as SetName, geometry, w: bb.max.x - bb.min.x, h: bb.max.y - bb.min.y, d: bb.max.z - bb.min.z, triangles };
      } catch (err) {
        console.info('마을 세트피스를 읽지 못했다(그 조각은 빠진다): ' + name, err instanceof Error ? err.message : err);
        return null;
      }
    })();
    townCache.set(name, p);
  }
  return p;
}

/** 늪 물: 탁한 갈녹색 + 떠다니는 이끼·연잎 얼룩 + 느린 잔물결 빛. 물가는 진흙빛 거품 */
function swampWaterMaterial(haze: THREE.Color): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.35, metalness: 0 });
  const wp = positionWorld.xz;
  const t = time;
  const w1 = mx_noise_float(vec3(wp.mul(0.35), t.mul(0.12))).mul(0.5).add(0.5);
  const w2 = mx_noise_float(vec3(wp.mul(1.4).add(vec2(t.mul(0.15), t.mul(-0.1))), 4.1)).mul(0.5).add(0.5);
  const pads = smoothstep(0.66, 0.7, mx_noise_float(vec3(wp.mul(0.55), 9.3)).mul(0.5).add(0.5));
  const scum = smoothstep(0.45, 0.8, mx_noise_float(vec3(wp.mul(0.12), 2.2)).mul(0.5).add(0.5));
  let col = mix(vec3(0.08, 0.12, 0.07), vec3(0.16, 0.2, 0.1), w1);
  col = mix(col, vec3(0.24, 0.32, 0.12), scum.mul(0.55));
  col = mix(col, vec3(0.2, 0.42, 0.14), pads.mul(0.85));
  m.colorNode = col;
  const glint = smoothstep(0.72, 0.8, w2).mul(float(1).sub(pads));
  m.emissiveNode = vec3(0.5, 0.62, 0.42).mul(glint.mul(0.14)).add(vec3(haze.r, haze.g, haze.b).mul(0.06));
  return m;
}

/** 구름 바다: 하늘빛 바탕에 느리게 흐르는 흰 구름 덩이와 푸른 그늘. 빛을 받아 스스로 밝다(그림자 없음) */
function cloudSeaMaterial(haze: THREE.Color): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 1, metalness: 0 });
  const wp = positionWorld.xz;
  const t = time;
  const big = mx_noise_float(vec3(wp.mul(0.045).add(vec2(t.mul(0.03), t.mul(0.012))), 1.7)).mul(0.5).add(0.5);
  const mid = mx_noise_float(vec3(wp.mul(0.14).add(vec2(t.mul(-0.05), t.mul(0.03))), 5.3)).mul(0.5).add(0.5);
  const fine = mx_noise_float(vec3(wp.mul(0.5).add(vec2(t.mul(0.08), t.mul(-0.04))), 8.1)).mul(0.5).add(0.5);
  const puff = smoothstep(0.32, 0.78, big.mul(0.55).add(mid.mul(0.35)).add(fine.mul(0.1)));
  let col = mix(vec3(0.4, 0.55, 0.8), vec3(0.88, 0.91, 0.98), puff);
  col = mix(col, vec3(0.7, 0.66, 0.9), smoothstep(0.55, 0.9, mid).mul(float(1).sub(puff)).mul(0.5));
  m.colorNode = col;
  m.emissiveNode = col.mul(0.26).add(vec3(haze.r, haze.g, haze.b).mul(0.06));
  return m;
}

/** 랜드마크 → 마을 키트 종류(없으면 null: 렌더하지 않거나 세트피스가 대신한다) */
function landmarkKit(kind: string): KitKind | null {
  switch (kind) {
    case 'shop': case 'jeweler': case 'alchemist': return 'v_shop';
    case 'forge': return 'v_forge';
    case 'trainer': return 'v_trainer';
    case 'board': return 'v_questboard';
    case 'gate': return 'v_gate';
    case 'tree': return 'tree';
    case 'statue': return 'statue';
    default: return null;
  }
}
/** 시설 건물 색 곱: 보석상은 푸른 차양, 연금술사는 초록 */
const FACILITY_TINT: Readonly<Record<string, number>> = { jeweler: 0xb4c8ff, alchemist: 0xbfe6a8 };

export class TownEnv {
  readonly root = new THREE.Group();
  private readonly owned: { dispose(): void }[] = [];
  private readonly batches: Batch[] = [];
  private started: Promise<EnvResult> | null = null;
  private readonly lastCull = new Float32Array([1e9, 1e9, 0]);
  private readonly look: TownLook;
  private npcs: VillageNpcs | null = null;
  private drift: { value: THREE.Vector3 } | null = null;

  constructor(private readonly map: MapDef, private readonly grid: MapGrid, private readonly quality: QualityLevel, private readonly town: number) {
    this.root.name = 'town-env-' + town;
    this.look = TOWN_LOOKS[town];
  }

  start(): Promise<EnvResult> {
    this.started ??= this.build();
    return this.started;
  }

  private add<T extends THREE.Object3D>(o: T | null): T | null {
    if (o) {
      this.root.add(o);
      const m = o as unknown as THREE.Mesh;
      if (m.geometry) this.owned.push(m.geometry);
      if (m.material) this.owned.push(m.material as THREE.Material);
    }
    return o;
  }

  private makeBatch(geo: THREE.BufferGeometry, mat: THREE.Material, list: readonly Inst[], shadow: boolean, name: string, cullR = CULL_RADIUS): void {
    const n = list.length;
    if (n === 0) return;
    const cutSrc = new Float32Array(n * 4);
    const cut = new THREE.InstancedBufferAttribute(new Float32Array(n * 4), 4);
    cut.setUsage(THREE.DynamicDrawUsage);
    const g = new THREE.BufferGeometry();
    if (geo.index) g.setIndex(geo.index);
    for (const a of ['position', 'normal', 'uv']) { const at = geo.getAttribute(a); if (at) g.setAttribute(a, at); }
    g.setAttribute('aCut', cut);
    const mesh = new THREE.InstancedMesh(g, mat, n);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    const matSrc = new Float32Array(n * 16), colSrc = new Float32Array(n * 3), pos = new Float32Array(n * 3);
    const col = new THREE.Color();
    list.forEach((p, i) => {
      compose(matSrc, i * 16, p.x, p.y, p.z, p.rot, 0, p.sx, p.sy, p.sz);
      cutSrc[i * 4] = p.x; cutSrc[i * 4 + 1] = p.z; cutSrc[i * 4 + 2] = p.cut; cutSrc[i * 4 + 3] = p.sy;
      col.setHex(p.tint);
      colSrc[i * 3] = col.r; colSrc[i * 3 + 1] = col.g; colSrc[i * 3 + 2] = col.b;
      mesh.setColorAt(i, col);
      pos[i * 3] = p.x; pos[i * 3 + 1] = p.z; pos[i * 3 + 2] = p.radius;
    });
    (mesh.instanceMatrix.array as Float32Array).set(matSrc);
    (cut.array as Float32Array).set(cutSrc);
    mesh.instanceMatrix.needsUpdate = true;
    mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.castShadow = shadow && this.quality !== 'low';
    mesh.receiveShadow = true;
    mesh.name = name;
    this.root.add(mesh);
    this.owned.push(g);
    this.batches.push({ cullR, mesh, cut, n, mat: matSrc, cutSrc, col: colSrc, pos });
  }

  private async build(): Promise<EnvResult> {
    const map = this.map, look = this.look, town = this.town;
    const rg = renderGrid(map, look);
    const mood = moodOf(map);
    const haze = new THREE.Color(mood.haze);
    const rnd = mulberry32(7100 + town * 131);
    const tints = TOWN_TINTS[town] ?? TOWN_TINTS[4];
    const density = DENSITY[this.quality];
    const region = town === 3 ? 'r3' : town === 4 ? 'r4' : null;
    const rlook = region ? REGION_LOOK[region] : null;

    // ── 읽을 것 모으기 ──
    const kitKinds = new Set<KitKind>(['v_house', 'village_house_b', 'v_lamppost', 'village_market_stall', 'village_banner_pole', 'crates', 'village_flower_planter']);
    for (const l of map.landmarks) { const k = landmarkKit(l.kind); if (k) kitKinds.add(k); }
    if (map.landmarks.some((l) => l.kind === 'gate')) kitKinds.add('arch');
    for (const p of look.props) kitKinds.add(p.kind as KitKind);
    if (town === 3) kitKinds.add('tree_lo');
    if (town === 2 || town === 5) kitKinds.add('rock_lo');
    const setNames = new Set<SetName>();
    for (const m of STYLE_MODULES[map.edgeStyle ?? 'cliff'] ?? []) setNames.add(m);
    for (const z of map.edgeZones ?? []) for (const m of STYLE_MODULES[z.style] ?? []) setNames.add(m);
    for (const s of map.sets ?? []) if (s.kind !== 'waterfall') setNames.add(s.kind as SetName);
    setNames.add('prop_rock');
    const townSetNames = new Set<TownSetName>(look.townSets.map((s) => s.kind));
    const kitList = [...kitKinds];
    const [floorTex, capTex, pano, atlas, kitAssets] = await Promise.all([
      track(loadTexture(look.floorTex).then((t) => t ?? loadTexture('village/tex/plaza_cobble.webp'))),
      track(loadTexture('r1/tex/floor_moss_stone.webp')),
      track(loadTexture(look.pano, true).then((t) => t ?? loadTexture('village/tex/bg_village_panorama.webp', true))),
      track(loadSetAtlas()),
      Promise.all(kitList.map((k) => track(loadKit(k, region)))),
    ]);
    const kit = new Map<KitKind, KitAsset>();
    kitList.forEach((k, i) => { const a = kitAssets[i]; if (a) kit.set(k, a); });
    const sets = new Map<SetName, SetAsset>();
    await Promise.all([...setNames].map(async (n) => { const a = await track(loadSet(n)); if (a) sets.set(n, a); }));
    const tsets = new Map<TownSetName, SetAsset>();
    await Promise.all([...townSetNames].map(async (n) => { const a = await track(loadTownSet(n)); if (a) tsets.set(n, a); }));

    // ── 원경 ──
    const bd = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), backdropMaterial(pano, haze, 2.5, 0.72, 90));
    bd.frustumCulled = false;
    bd.renderOrder = -1000;
    this.add(bd);

    // ── 바닥 윗면(다리 자리는 비운다) ──
    const bridgeKinds: SetName[] = ['bridge', 'bridge_wood', 'bridge_root'];
    const bridgeOk = (map.sets ?? []).every((s) => !bridgeKinds.includes(s.kind as SetName) || sets.has(s.kind as SetName));
    const bareZones: Rect[] = bridgeOk ? (map.edgeZones ?? []).filter((z) => z.style === 'bare').map((z) => z.rect) : [];
    const inBare = (x: number, z: number): boolean => bareZones.some((r) => x >= r.x0 && x <= r.x1 && z >= r.z0 && z <= r.z1);
    const field = buildField(rg, 5100 + town * 7);
    const contour = contourOf(field, rg, bareZones.length ? inBare : undefined);
    const proxData = edgeProximity(rg, 3);
    const prox: ProximityTex = { tex: dataTexture(proxData, rg.width, rg.height), originX: rg.originX, originZ: rg.originZ, width: rg.width, height: rg.height };
    this.owned.push(prox.tex);
    {
      const pos: number[] = [];
      for (let i = 0; i < contour.band.length; i += 2) pos.push(contour.band[i], 0, contour.band[i + 1]);
      for (const r of contour.interior) pos.push(r.x0, 0, r.z0, r.x0, 0, r.z1, r.x1, 0, r.z1, r.x0, 0, r.z0, r.x1, 0, r.z1, r.x1, 0, r.z0);
      const arr = new Float32Array(pos);
      for (let t = 0; t < arr.length; t += 9) {
        const ax = arr[t], az = arr[t + 2], bx = arr[t + 3], bz = arr[t + 5], cx = arr[t + 6], cz = arr[t + 8];
        if ((bz - az) * (cx - ax) - (bx - ax) * (cz - az) < 0) for (let k = 0; k < 3; k++) { const o = t + 3 + k; const p = arr[o]; arr[o] = arr[o + 3]; arr[o + 3] = p; }
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(arr, 3));
      g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(arr.length).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
      const top = new THREE.Mesh(g, stageFloorMaterial(floorTex, prox, look.floorKey));
      top.receiveShadow = true;
      top.name = 'town-floor';
      this.add(top);
    }
    {
      const cells = cellsOf([...map.floors, ...look.islands]);
      for (const k of [...cells]) { const [cx, cz] = k.split(',').map(Number); if (inBare(cx + 0.5, cz + 0.5)) cells.delete(k); }
      const parts: number[] = [];
      for (const r of rectsOfCells(cells)) parts.push(r.x0, -0.04, r.z0, r.x0, -0.04, r.z1, r.x1, -0.04, r.z1, r.x0, -0.04, r.z0, r.x1, -0.04, r.z1, r.x1, -0.04, r.z0);
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(parts, 3));
      const under = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: new THREE.Color(0.16, 0.14, 0.12) }));
      under.name = 'town-underlay';
      this.add(under);
    }

    // ── 가장자리: 둑/흉벽 단면 · 트림 · 풀 카드 ──
    const depth = -look.liquidY + 0.35;
    const cliffPos: number[] = [], cliffIdx: number[] = [];
    const styleAt = (x: number, z: number): string => (inBare(x, z) ? 'bare' : edgeStyleAt(map, x, z));
    const buildSide = (chain: Chain): void => {
      const dense = resample(chain, 1.0);
      if (dense.length < 2) return;
      const rows = 3;
      let prev = -1;
      for (let i = 0; i < dense.length; i++) {
        const s = dense[i];
        if (styleAt(s.x, s.z) === 'bare') { prev = -1; continue; }
        const base = cliffPos.length / 3;
        for (let k = 0; k <= rows; k++) {
          const t = k / rows;
          const n1 = valueNoise(s.x * 0.8 + k, s.z * 0.8 - k, 33) - 0.45;
          cliffPos.push(s.x + s.nx * (k > 0 ? n1 * 0.35 * t : 0), k === 0 ? 0.02 : -depth * t, s.z + s.nz * (k > 0 ? n1 * 0.35 * t : 0));
        }
        if (prev >= 0) for (let k = 0; k < rows; k++) { const a = prev + k, b = prev + k + 1, c = base + k, d = base + k + 1; cliffIdx.push(a, c, b, b, c, d); }
        prev = base;
      }
    };
    const trims = new Map<SetName, Inst[]>();
    const pushTrim = (n: SetName, i: Inst): void => { let a = trims.get(n); if (!a) trims.set(n, (a = [])); a.push(i); };
    const tufts: Emitter[] = [], glows: Emitter[] = [], flames: Emitter[] = [], lamps: Emitter[] = [];
    const ivy: (Emitter & { rot: number; w: number })[] = [];
    const trimTints = TRIM_TINT[town];
    const spacing = 2.4;
    for (const chain of contour.chains) {
      buildSide(chain);
      for (const s of resample(chain, spacing)) {
        const style = styleAt(s.x, s.z);
        if (style === 'bare') continue;
        const yaw = Math.atan2(s.nx, s.nz);
        if (style === 'roots' && rnd() < 0.5) continue;
        const mods = STYLE_MODULES[style] ?? STYLE_MODULES.cliff;
        const name = mods[Math.floor(rnd() * mods.length)];
        const a = sets.get(name);
        if (a) {
          const lenK = (spacing / Math.max(0.5, a.w)) * (1 + rnd() * 0.12);
          const sc = (0.92 + rnd() * 0.2) * (style === 'roots' ? 0.8 : 1);
          pushTrim(name, { x: s.x - s.nx * 0.12, y: 0, z: s.z - s.nz * 0.12, rot: yaw + (rnd() - 0.5) * 0.12, sx: lenK, sy: sc, sz: sc, tint: trimTints[Math.floor(rnd() * trimTints.length)], cut: 1, radius: spacing });
        }
        // 가장자리 안쪽 풀·서리·갈대 카드
        const tn = Math.round(1.6 * density + rnd());
        for (let k = 0; k < tn; k++) {
          const dd = 0.5 + rnd() * 1.3, u = (rnd() - 0.5) * spacing;
          tufts.push({ x: s.x - s.nx * dd + s.tx * u, y: 0, z: s.z - s.nz * dd + s.tz * u, s: 0.42 + rnd() * 0.35, v: rnd() });
        }
        // ② 용암 물가: 아래에서 올라오는 붉은 빛 망울(드물게) · ④ 뿌리 단면 덩굴
        if (town === 2 && rnd() < 0.22) glows.push({ x: s.x + s.nx * 1.3, y: look.liquidY + 0.3, z: s.z + s.nz * 1.3, s: 2.6 + rnd() * 1.6, v: rnd() });
        if (town === 4 && rnd() < 0.35 * density) ivy.push({ x: s.x + s.nx * 0.2, y: 0.1, z: s.z + s.nz * 0.2, s: 0.8 + rnd() * 1.2, v: rnd(), rot: yaw, w: 1.2 + rnd() * 0.8 });
      }
    }
    if (cliffIdx.length > 0) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(cliffPos, 3));
      g.setIndex(cliffIdx);
      g.computeVertexNormals();
      const side = new THREE.Mesh(g, stageCliffMaterial(town === 4 ? 'f' : town === 2 || town === 5 ? 'm' : 'b', haze, -depth));
      side.name = 'town-side';
      this.add(side);
    }

    // ── 액체 면(용암·바다·늪): 판 둘레를 넉넉히 덮는 한 장 ──
    {
      const cx = rg.originX + rg.width / 2, cz = rg.originZ + rg.height / 2, size = Math.max(rg.width, rg.height) + 180;
      const g = new THREE.PlaneGeometry(size, size);
      g.rotateX(-PI / 2);
      g.translate(cx, look.liquidY, cz);
      let mat: THREE.Material;
      if (look.liquid === 'lava') mat = magmaMaterial();
      else if (look.liquid === 'cloud') mat = cloudSeaMaterial(haze);
      else {
        const shore: ProximityTex = { tex: dataTexture(shoreDistance(rg, 8), rg.width, rg.height), originX: rg.originX, originZ: rg.originZ, width: rg.width, height: rg.height };
        this.owned.push(shore.tex);
        mat = look.liquid === 'sea' || look.liquid === 'oasis' ? stageWaterMaterial(shore, haze) : swampWaterMaterial(haze);
      }
      const liquid = this.add(new THREE.Mesh(g, mat))!;
      liquid.receiveShadow = look.liquid !== 'lava' && look.liquid !== 'cloud';
      liquid.name = 'town-liquid-' + look.liquid;
    }

    // ── 다리·원경 세트피스(지도 앵커) ──
    const placed = new Map<SetName, Inst[]>();
    const push = (n: SetName, i: Inst): void => { let a = placed.get(n); if (!a) placed.set(n, (a = [])); a.push(i); };
    for (const s of map.sets ?? []) {
      const n = s.kind as SetName, a = sets.get(n);
      if (!a) continue;
      const sc = s.scale ?? 1, isBridge = bridgeKinds.includes(n);
      const sz = isBridge && s.len ? s.len / Math.max(1, a.d) : sc;
      const far = n.startsWith('far_');
      push(n, { x: s.x, y: s.y ?? 0, z: s.z, rot: s.rot, sx: isBridge ? 1 : sc, sy: isBridge ? 1 : sc, sz, tint: far ? FAR_TINT[town] : town === 2 ? 0x8c8088 : 0xffffff, cut: far ? 0 : 1, radius: Math.max(a.w, a.d, a.h) * 0.5 * Math.max(sc, sz) });
    }

    // ── 시설 건물·랜드마크(마을 키트) ──
    const kitInst = new Map<KitKind, Inst[]>();
    const replaced = new Set<number>();
    const tops = new Map<number, number>();
    const contacts: { x: number; y: number; z: number; rx: number; rz: number; rot: number; a: number }[] = [];
    const putKit = (kind: KitKind, x: number, y: number, z: number, rot: number, h: number, w = 0, d = 0, tint = 0xffffff, cut: CutMode = 1, contact = 0): number => {
      const k = kit.get(kind);
      if (!k) return -1;
      let sx: number, sy: number, sz: number;
      if (h > 0 && w === 0) { sx = sy = sz = h / k.h; }
      else { const s = Math.min(w / k.w, d / k.d); sx = sy = sz = s; }
      let a = kitInst.get(kind);
      if (!a) kitInst.set(kind, (a = []));
      const r = 0.5 * Math.max(k.w * sx, k.d * sz, k.h * sy);
      a.push({ x, y, z, rot, sx, sy, sz, tint, cut, radius: r });
      if (contact > 0) contacts.push({ x, y, z, rx: Math.min(k.w * sx, 6) * 0.55 + 0.5, rz: Math.min(k.d * sz, 6) * 0.55 + 0.5, rot, a: contact });
      return k.h * sy;
    };
    const npcs = townNpcs(town);
    const quarter = (a: number): number => Math.round(a / (PI / 2)) * (PI / 2);
    const overridden = new Set(look.townSets.filter((s) => s.landmark !== undefined).map((s) => s.landmark!));
    map.landmarks.forEach((l, i) => {
      const w = l.rect.x1 - l.rect.x0, d = l.rect.z1 - l.rect.z0;
      const cx = (l.rect.x0 + l.rect.x1) / 2, cz = (l.rect.z0 + l.rect.z1) / 2;
      if (l.kind === 'prop') { replaced.add(i); return; }
      if (overridden.has(i)) return;
      const kk = landmarkKit(l.kind);
      if (l.kind === 'shop' || l.kind === 'forge' || l.kind === 'trainer' || l.kind === 'jeweler' || l.kind === 'alchemist') {
        // 정면(+Z)이 그 시설 NPC 쪽(문 앞)을 본다
        const n = npcs.find((p) => p.role === l.kind);
        const rot = n ? quarter(Math.atan2(n.x - cx, n.z - cz)) : 0;
        const side = Math.abs(Math.sin(rot)) > 0.5;
        const top = putKit(kk!, cx, 0, cz, rot, 0, side ? d : w, side ? w : d, FACILITY_TINT[l.kind] ?? tints.facility, 2, 0.5);
        if (top > 0) { replaced.add(i); tops.set(i, top); }
      } else if (l.kind === 'board') {
        const top = putKit('v_questboard', cx, 0, cz, 0, 3.2, 0, 0, 0xffffff, 2, 0.55);
        if (top > 0) { replaced.add(i); tops.set(i, top); }
      } else if (l.kind === 'gate') {
        // 문 정면(+Z)이 걸을 수 있는 바닥(자기 출구) 쪽을 본다(마을마다 자리·방향이 다르다, STAGE-WORLD-PRD 5.4).
        // 로컬(정면 +z) → 월드: x' = x cos + z sin, z' = −x sin + z cos. 문 폭은 정면에 수직인 변
        const rot = gateFront(map.floors, l.rect), c = Math.cos(rot), sn = Math.sin(rot);
        const wx = (lx: number, lz: number): number => cx + lx * c + lz * sn;
        const wz = (lx: number, lz: number): number => cz - lx * sn + lz * c;
        const span = Math.abs(sn) > 0.5 ? d : w;
        const hot = town === 2 || l.label === '잿불 협곡';
        // 카메라 쪽 디더(2): 북쪽을 보는 문(③ 잔교 끝·④ 문 데크)은 문 앞에 선 영웅과 카메라 사이에 서므로 비친다. 영웅 앞쪽 문은 그대로 보인다
        const top = putKit('v_gate', wx(0, 1.6), 0, wz(0, 1.6), rot, 7.4, 0, 0, hot ? 0xff7040 : tints.gate, 2, 0.45);
        if (top > 0) { replaced.add(i); tops.set(i, top); }
        else if (putKit('arch', cx, 0, cz, rot, 7.2, (4.43 * (span + 0.7)) / 3.36, 2.0) > 0) replaced.add(i);
        if (hot) for (const s of [-1, 1]) { const bx = wx(s * 3.1, 2.2), bz = wz(s * 3.1, 2.2); putKit('brazier', bx, 0, bz, 0, 1.7, 0, 0, 0x75483b, 0); flames.push({ x: bx, y: 1.65, z: bz, s: 1.1, v: 0.5 }); }
      } else if (l.kind === 'tree') {
        const top = putKit('tree', cx, 0, cz, rnd() * PI * 2, l.height * 1.15, 0, 0, 0xffffff, 2, 0.6);
        if (top > 0) replaced.add(i);
      } else if (l.kind === 'stall') {
        const sideX = Math.abs(w) <= Math.abs(d);
        const rot = sideX ? (cx < 0 ? PI / 2 : -PI / 2) : 0;
        const ok = putKit('village_market_stall', cx, 0, cz, rot, 2.6, 0, 0, tints.stall, 1, 0.55) > 0;
        putKit('crates', cx + (sideX ? 0 : 1.1), 0, cz + (sideX ? 1.2 : 0.6), rnd() * 0.4, 0.85, 0, 0, 0xf2e2c8, 1);
        if (ok) replaced.add(i);
      } else if (l.kind === 'decor') {
        putKit('v_lamppost', cx, 0, cz - 0.4, 0, 3.8, 0, 0, town === 2 ? 0x8c7a74 : 0xffffff, 1, 0.5);
        putKit('village_banner_pole', cx + (cx < 0 ? -0.9 : 0.9), 0, cz + 0.6, cx < 0 ? PI / 2 : -PI / 2, 4.2, 0, 0, town === 2 ? 0xd08a70 : 0xffffff, 1);
        lamps.push({ x: cx, y: 3.3, z: cz - 0.4, s: 1.1, v: rnd() });
        replaced.add(i);
      } else if (l.kind === 'statue') {
        if (putKit('statue', cx, 0, cz, 0, 4.8, 0, 0, 0xffffff, 2, 0.6) > 0) replaced.add(i);
      }
    });

    // ── 소품(가로등·화로·상자·깃발·나무·바위) ──
    for (const p of look.props) {
      const kind = p.kind as TownPropKind as KitKind;
      const y = p.y ?? 0;
      putKit(kind, p.x, y, p.z, p.rot, p.h, 0, 0, p.tint ?? 0xffffff, kind === 'tree' ? 2 : 1, y === 0 ? 0.5 : 0);
      if (kind === 'brazier') flames.push({ x: p.x, y: y + p.h * 0.96, z: p.z, s: 0.9, v: rnd() });
      if (kind === 'v_lamppost') lamps.push({ x: p.x, y: y + p.h * 0.86, z: p.z, s: 1.1, v: rnd() });
      if (kind === 'mushrooms') glows.push({ x: p.x, y: y + p.h * 0.55, z: p.z, s: p.h * 2.3, v: rnd() });
    }

    // ── 섬 위 집(마을 바깥 풍경): 섬 칸을 간격마다 훑어 집 두 종류를 섞는다. 정면은 광장 쪽 ──
    if (look.houses.spacing > 0) {
      const rh = mulberry32(900 + town);
      for (const isl of look.islands) {
        const sp = look.houses.spacing;
        for (let z = isl.z0 + sp / 2; z < isl.z1 - 1.5; z += sp) {
          for (let x = isl.x0 + sp / 2; x < isl.x1 - 1.5; x += sp) {
            const jx = x + (rh() - 0.5) * 1.6, jz = z + (rh() - 0.5) * 1.6;
            if (rh() < 0.12) { putKit(town === 3 ? 'tree_lo' : 'rock_lo', jx, 0, jz, rh() * PI * 2, town === 3 ? 6 + rh() * 1.5 : 1.6 + rh(), 0, 0, town === 2 ? 0x5a4c50 : town === 5 ? 0xd6b478 : 0xffffff, 2); continue; }
            const rot = quarter(Math.atan2(4 - jx, -4 - jz));
            const h = look.houses.h[0] + rh() * (look.houses.h[1] - look.houses.h[0]);
            putKit(rh() < 0.5 ? 'v_house' : 'village_house_b', jx, 0, jz, rot, h, 0, 0, look.houses.tint, 2, 0.45);
          }
        }
      }
    }

    // ── 마을 전용 Blender 세트피스 ──
    const tplaced = new Map<TownSetName, Inst[]>();
    for (const s of look.townSets) {
      const a = tsets.get(s.kind);
      if (!a) continue;
      const sc = s.scale ?? 1;
      const y = s.y ?? (s.kind === 'ship' ? look.liquidY + 0.15 : 0);
      let arr = tplaced.get(s.kind);
      if (!arr) tplaced.set(s.kind, (arr = []));
      arr.push({ x: s.x, y, z: s.z, rot: s.rot, sx: sc, sy: sc, sz: sc, tint: s.tint ?? (s.kind === 'obsidian_spire' ? 0xb0a8b4 : 0xffffff), cut: TOWN_SET_STYLE[s.kind].cut, radius: Math.max(a.w, a.d, a.h) * 0.55 * sc });
      if (s.landmark !== undefined) { replaced.add(s.landmark); tops.set(s.landmark, a.h * sc); }
      if (s.kind !== 'ship' && s.kind !== 'stilt_house' && !(s.y !== undefined && s.y < -1)) contacts.push({ x: s.x, y: 0, z: s.z, rx: Math.min(a.w, 7) * 0.55 * sc + 0.5, rz: Math.min(a.d, 7) * 0.55 * sc + 0.5, rot: s.rot, a: 0.5 });
      const c = Math.cos(s.rot), sn = Math.sin(s.rot);
      for (const L of s.noLight ? [] : TOWN_SET_LIGHTS[s.kind] ?? []) {
        // 로컬(정면 +z) → 월드: x' = x cos + z sin, z' = −x sin + z cos
        const wx = s.x + (L.x * c + L.z * sn) * sc, wz = s.z + (-L.x * sn + L.z * c) * sc, wy = y + L.y * sc;
        if (L.kind === 'flame') flames.push({ x: wx, y: wy, z: wz, s: L.s * sc, v: rnd() });
        else glows.push({ x: wx, y: wy, z: wz, s: L.s * sc, v: rnd() });
      }
    }

    // 마을 풍경이 정한 추가 불빛 망울(샘·수정)
    for (const L of look.lights ?? []) glows.push({ x: L.x, y: L.y, z: L.z, s: L.s, v: rnd() });

    // ── 인스턴싱 ──
    const matCache = new Map<string, THREE.MeshStandardNodeMaterial>();
    const setMat = (clip: boolean, cap: readonly [number, number, number]): THREE.Material => {
      const k = (clip ? 'c' : 'n') + cap.join(',');
      let m = matCache.get(k);
      if (!m) { m = setMaterial(atlas as SetAtlas, { clip, cap, emissive: true }); matCache.set(k, m); this.owned.push(m); }
      return m;
    };
    const STONE: readonly [number, number, number] = [0.42, 0.44, 0.32];
    for (const [n, list] of [...trims, ...placed]) {
      const a = sets.get(n);
      if (!a) continue;
      const far = n.startsWith('far_');
      this.makeBatch(a.geometry, setMat(!far, STONE), list, bridgeKinds.includes(n), 'set-' + n, far ? 160 : CULL_RADIUS);
    }
    for (const [n, list] of tplaced) {
      const a = tsets.get(n)!;
      const st = TOWN_SET_STYLE[n];
      this.makeBatch(a.geometry, setMat(st.clip, st.cap), list, st.shadow, 'town-' + n, 90);
    }
    for (const [kind, list] of kitInst) {
      const k = kit.get(kind)!;
      const mat = kitMaterial(k, kind, capTex, false, k.region !== 'base' ? rlook : null);
      this.owned.push(mat);
      const tall = kind === 'v_house' || kind === 'village_house_b' || kind === 'tree_lo' || kind === 'v_gate';
      this.makeBatch(k.geometry, mat, list, kind !== 'crates' && kind !== 'rock_lo', 'kit-' + kind, tall ? 90 : CULL_RADIUS);
    }
    this.add(contactField(contacts));

    // ── 효과 ──
    this.add(cardField(tufts, look.cards, town === 2 || town === 5));
    if (glows.length > 0) {
      // 불빛 망울: ② 잿불 주황, ④ 가마솥 초록·등불 주황을 따로(색이 다르다)
      const warm = glows.filter((g) => !(town === 4 && g.s >= 2 && g.s < 2.3));
      const green = glows.filter((g) => town === 4 && g.s >= 2 && g.s < 2.3);
      if (warm.length) this.add(glowField(warm, tints.glow));
      if (green.length) this.add(glowField(green, [0.45, 1.0, 0.3]));
    }
    if (lamps.length > 0) this.add(glowField(lamps, tints.lamp));
    if (flames.length > 0) this.add(flameField(flames));
    if (ivy.length > 0) this.add(ivyField(ivy));
    if (look.drift) {
      const d = driftField(look.drift, density);
      if (d) { this.add(d.mesh); this.drift = d.center; }
    }

    // ── 주민 ──
    const people = new VillageNpcs();
    await people.load(townSpots(town));
    this.root.add(people.root);
    this.npcs = people;
    // 키트를 못 읽은 건물은 replaced에 없어 그레이박스로 남는다
    return { replaced, tops, walls: true, floor: true };
  }

  /** 매 프레임 주민 애니메이션 */
  animate(nowMs: number): void { this.npcs?.update(nowMs); }

  cull(camera: THREE.PerspectiveCamera | null): void {
    const b = this.batches;
    if (b.length === 0) return;
    if (!camera) { for (const k of b) this.fill(k, -1e9, 1e9, -1e9, 1e9, 0, 0, 1e9); this.lastCull[0] = 1e9; return; }
    const cp = camera.position, lc = this.lastCull;
    if (Math.abs(cp.x - lc[0]) < 1.5 && Math.abs(cp.z - lc[1]) < 1.5 && Math.abs(cp.y - lc[2]) < 1.5) return;
    lc[0] = cp.x; lc[1] = cp.z; lc[2] = cp.y;
    let x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
    camera.updateMatrixWorld();
    for (let c = 0; c < 4; c++) {
      _ndc.set(c & 1 ? 1 : -1, c & 2 ? 1 : -1, 0.5).unproject(camera);
      _dir.copy(_ndc).sub(cp).normalize();
      for (let hgt = 0; hgt <= 7; hgt += 7) {
        const t = _dir.y < -1e-3 ? Math.min(120, (cp.y - hgt) / -_dir.y) : 120;
        const x = cp.x + _dir.x * t, z = cp.z + _dir.z * t;
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (z < z0) z0 = z; if (z > z1) z1 = z;
      }
    }
    _ndc.set(0, 0, 0.5).unproject(camera);
    _dir.copy(_ndc).sub(cp).normalize();
    const tc = _dir.y < -1e-3 ? Math.min(70, cp.y / -_dir.y) : 30;
    const cx = cp.x + _dir.x * tc, cz = cp.z + _dir.z * tc;
    for (const k of b) this.fill(k, x0 - CULL_MARGIN, x1 + CULL_MARGIN, z0 - CULL_MARGIN, z1 + CULL_MARGIN, cx, cz, k.cullR);
    if (this.drift) this.drift.value.set(cx, 0, cz);
  }

  private fill(k: Batch, x0: number, x1: number, z0: number, z1: number, cx: number, cz: number, radius: number): void {
    const dm = k.mesh.instanceMatrix.array as Float32Array, dc = k.cut.array as Float32Array, dcol = k.mesh.instanceColor!.array as Float32Array;
    const { pos, mat, cutSrc, col } = k;
    let m = 0;
    for (let i = 0; i < k.n; i++) {
      const x = pos[i * 3], z = pos[i * 3 + 1], r = pos[i * 3 + 2];
      if (x + r < x0 || x - r > x1 || z + r < z0 || z - r > z1) continue;
      if (Math.hypot(x - cx, z - cz) - r > radius) continue;
      for (let j = 0; j < 16; j++) dm[m * 16 + j] = mat[i * 16 + j];
      for (let j = 0; j < 4; j++) dc[m * 4 + j] = cutSrc[i * 4 + j];
      for (let j = 0; j < 3; j++) dcol[m * 3 + j] = col[i * 3 + j];
      m++;
    }
    k.mesh.count = m;
    k.mesh.instanceMatrix.needsUpdate = true;
    k.cut.needsUpdate = true;
    k.mesh.instanceColor!.needsUpdate = true;
  }

  dispose(): void {
    this.npcs?.dispose();
    for (const o of this.owned) o.dispose();
    this.owned.length = 0;
    this.root.clear();
  }
}
